import React from "react";
import { Badge, Box, Button, Icon } from "@salestrak/ui";

import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { pluralize } from "#app/components/issue-view/activity/activity-utils";

import { DetailFieldRow } from "./detail-field-row";
import {
  EntityDetailShell,
  EntityDetailHeader,
  EntityDetailCard,
} from "./entity-detail-shell";

interface IssueTypeDetailDialogProps {
  issueTypeId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function IssueTypeDetailDialog({
  issueTypeId,
  open,
  onOpenChange,
}: IssueTypeDetailDialogProps) {
  const { issueTypeMap } = useWorkspaceData();
  const navigate = useAppNavigate();

  const issueType = issueTypeId ? issueTypeMap.get(issueTypeId) : undefined;

  if (!issueType) return null;

  const fieldCount = issueType.fields?.length ?? 0;

  const openSettings = () => {
    onOpenChange(false);
    navigate.push(`/settings/issue-types/${issueType.issueTypeId}`);
  };

  const header = (
    <EntityDetailHeader
      entity={{ kind: "issueType", id: issueType.issueTypeId, issueType }}
      padded
      badge={
        issueType.isDefault ? (
          <Badge size="sm" variant="secondary">
            Default
          </Badge>
        ) : undefined
      }
    />
  );

  const factsCard = (
    <EntityDetailCard>
      <Box display="grid" columns={1} gap="lg" className="sm:grid-cols-2">
        <DetailFieldRow
          icon="text"
          label="Description"
          value={issueType.description}
          truncate={2}
          copyable
        />
        <DetailFieldRow
          icon="list"
          label="Fields"
          value={`${fieldCount} ${pluralize("field", { count: fieldCount })}`}
        />
      </Box>
    </EntityDetailCard>
  );

  const footer = (
    <Button className="w-full" onClick={openSettings}>
      <Icon name="settings" />
      Open settings
    </Button>
  );

  return (
    <EntityDetailShell
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      srDescription={`${issueType.name} issue type details`}
      body={factsCard}
      footer={footer}
    />
  );
}
